import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { Loader2, RefreshCcw, Send } from "lucide-react";
import {
  listShareAttempts,
  retryShareNow,
} from "@/lib/api/social-queue.functions";

export const Route = createFileRoute(
  "/_authenticated/portal/admin/share-attempts",
)({
  component: ShareAttemptsPage,
});

type Status = "all" | "pending" | "success" | "failed" | "abandoned";

function ShareAttemptsPage() {
  const [status, setStatus] = useState<Status>("all");
  const listFn = useServerFn(listShareAttempts);
  const retryFn = useServerFn(retryShareNow);
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["share-attempts", status],
    queryFn: () => listFn({ data: { status, limit: 100 } }),
  });

  const retry = useMutation({
    mutationFn: (id: string) => retryFn({ data: { id } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["share-attempts"] }),
  });

  function badge(s: string) {
    const styles =
      s === "success"
        ? "bg-emerald-50 text-emerald-700"
        : s === "pending"
          ? "bg-amber-50 text-amber-700"
          : s === "abandoned"
            ? "bg-muted text-muted-foreground"
            : "bg-rose-50 text-rose-700";
    return (
      <span
        className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${styles}`}
      >
        {s}
      </span>
    );
  }

  return (
    <div className="container-narrow space-y-6 py-10">
      <header>
        <p className="text-xs uppercase tracking-[0.3em] text-gold">
          Admin · Social
        </p>
        <h1 className="mt-1 font-display text-3xl text-navy">
          Facebook share retry queue
        </h1>
        <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
          Background worker retries failed shares with exponential backoff
          (1 min → 2 → 4 → … up to 6 hours). Use "Retry now" to push an
          attempt to the head of the queue.
        </p>
      </header>

      <div className="inline-flex items-center gap-1 rounded-full border border-border bg-card p-1 text-xs">
        {(["all", "pending", "success", "failed", "abandoned"] as Status[]).map(
          (s) => (
            <button
              key={s}
              onClick={() => setStatus(s)}
              className={`rounded-full px-3 py-1 capitalize transition ${
                status === s
                  ? "bg-navy text-white"
                  : "text-foreground/70 hover:text-navy"
              }`}
            >
              {s}
            </button>
          ),
        )}
      </div>

      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : (
        <ul className="space-y-2">
          {(data ?? []).map((row: any) => (
            <li
              key={row.id}
              className="rounded-xl border border-border bg-card p-3 text-sm shadow-soft"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="flex items-center gap-2">
                    {badge(row.status)}
                    <span className="text-xs text-muted-foreground">
                      {row.kind} · attempt {row.attempt}/{row.max_attempts ?? 6}
                    </span>
                  </p>
                  <p className="mt-1 line-clamp-2 text-foreground/80">
                    {row.caption}
                  </p>
                  <a
                    href={row.share_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-1 block truncate text-[11px] text-navy underline"
                  >
                    {row.share_url}
                  </a>
                  <p className="mt-1 text-[11px] text-muted-foreground">
                    Created {new Date(row.created_at).toLocaleString("en-ZA")}
                    {row.next_retry_at && row.status === "pending" && (
                      <>
                        {" "}
                        · next retry{" "}
                        {new Date(row.next_retry_at).toLocaleString("en-ZA")}
                      </>
                    )}
                    {row.succeeded_at && (
                      <>
                        {" "}
                        · succeeded{" "}
                        {new Date(row.succeeded_at).toLocaleString("en-ZA")}
                      </>
                    )}
                  </p>
                  {row.last_error && (
                    <p className="mt-1 line-clamp-2 text-[11px] text-rose-700">
                      Last error: {row.last_error}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <a
                    href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(row.share_url)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 rounded-full border border-border bg-background px-3 py-1 text-[11px] font-medium hover:border-navy hover:text-navy"
                  >
                    <Send className="h-3 w-3" /> Open sharer
                  </a>
                  {row.status !== "success" && (
                    <button
                      onClick={() => retry.mutate(row.id)}
                      disabled={retry.isPending}
                      className="inline-flex items-center gap-1 rounded-full bg-navy px-3 py-1 text-[11px] font-semibold text-white hover:opacity-90 disabled:opacity-50"
                    >
                      <RefreshCcw className="h-3 w-3" /> Retry now
                    </button>
                  )}
                </div>
              </div>
            </li>
          ))}
          {(data ?? []).length === 0 && (
            <li className="text-sm text-muted-foreground">
              No share attempts in this view.
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
